/** Публичная страница. Примеры телеметрии явно помечены как демонстрационные. */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Flag,
  Footprints,
  Gauge,
  MapPin,
  MonitorPlay,
  Navigation,
  Route as RouteIcon,
  Satellite,
  Smartphone,
  Thermometer,
  Timer,
  Wind,
} from 'lucide-react';
import { Logo } from '../components/Logo';

// Обе линии сходятся в точке текущей позиции; маркеры стоят на концах путей.
const DEMO_COVERED = 'M 28 126 C 62 126, 78 86, 126 86 H 178';
const DEMO_REMAINING = 'M 178 86 C 214 86, 214 44, 258 44 H 292';

export function LandingPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex flex-col">
        <Hero />
        <OverlayShowcase />
        <BuiltForIrl />
        <TelemetryBoard />
        <Personalization />
        <FinalCta />
      </main>
      <SiteFooter />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Шапка
// ─────────────────────────────────────────────────────────────

function SiteHeader() {
  return (
    <header className="sticky top-0 z-30 border-b border-edge/70 bg-base/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 lg:px-10">
        <Logo />

        <nav className="flex items-center gap-2" aria-label="Основная навигация">
          <a
            href="#widget"
            className="hidden text-sm text-muted transition-colors hover:text-ink sm:block"
          >
            Виджет
          </a>
          <a
            href="#telemetry"
            className="hidden px-3 text-sm text-muted transition-colors hover:text-ink sm:block"
          >
            Телеметрия
          </a>
          <Link to="/login" className="btn-primary">
            Начать бесплатно
          </Link>
        </nav>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// Первый экран
// ─────────────────────────────────────────────────────────────

/**
 * Асимметричная композиция: слева редакционный блок, справа
 * картографическая сцена. Пропорции неравные — не две равные колонки.
 */
function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-edge">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-center lg:gap-16 lg:px-10 lg:py-24">
        <div className="flex flex-col gap-7">
          <p className="text-sm font-medium text-muted">
            GPS для IRL-стримов
          </p>

          <h1 className="text-4xl font-extrabold leading-[1.04] tracking-tight text-ink sm:text-5xl lg:text-display">
            Маршрут и телеметрия
            <br />
            <span className="text-lime">в OBS.</span>
          </h1>

          <p className="max-w-xl text-base leading-relaxed text-muted">
            GPS Tracker показывает зрителям маршрут, погоду, ветер и прогресс
            движения прямо в OBS. Координаты приходят с твоего телефона,
            расстояние считается по пешеходной сети, а не по прямой линии.
          </p>

          <div className="flex flex-wrap gap-3">
            <Link to="/login" className="btn-primary px-5 py-3">
              Начать бесплатно
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <a href="#widget" className="btn-ghost px-5 py-3">
              <MonitorPlay className="h-4 w-4" aria-hidden />
              Посмотреть виджет
            </a>
          </div>

          {/* Технические подписи вместо маркетинговых обещаний. */}
          <dl className="flex flex-wrap gap-x-10 gap-y-4 border-t border-edge pt-6">
            {[
              { term: 'Вход', value: 'Через Twitch' },
              { term: 'Маршрут', value: 'Пешеходная сеть' },
              { term: 'Данные', value: 'В реальном времени' },
            ].map(({ term, value }) => (
              <div key={term} className="flex flex-col gap-1">
                <dt className="stat-label">{term}</dt>
                <dd className="font-mono text-sm font-semibold text-ink">{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <HeroMap />
      </div>
    </section>
  );
}

/**
 * Картографическая сцена первого экрана.
 *
 * Маршрут прорисовывается анимацией `draw-route`, GPS-маркер стоит на
 * текущей точке. Анимация отключается через prefers-reduced-motion
 * правилом в styles.css.
 */
function HeroMap() {
  return (
    <figure>
      <div className="overflow-hidden rounded-xl border border-edge bg-surface">
        <div className="flex items-center justify-between gap-3 px-5 pt-5">
          <span className="flex items-center gap-2 text-xs font-medium text-lime">
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
            В пути
          </span>
          <span className="font-mono text-[0.625rem] text-muted">59.93284, 30.34012</span>
        </div>

        <svg viewBox="0 0 320 160" className="h-auto w-full" role="img"
          aria-label="Схема демонстрационного маршрута: старт, текущая позиция и цель">
          <path d={DEMO_COVERED} fill="none" stroke="currentColor" className="text-lime"
            strokeWidth="2.5" strokeLinecap="round" />
          <path d={DEMO_REMAINING} fill="none" stroke="currentColor" className="text-violet"
            strokeWidth="2.5" strokeDasharray="4 6" strokeLinecap="round" />
          <circle cx="28" cy="126" r="4.5" className="fill-surface stroke-lime" strokeWidth="2" />
          <circle cx="178" cy="86" r="4.5" className="fill-lime stroke-surface" strokeWidth="2" />
          <circle cx="292" cy="44" r="4.5" className="fill-surface stroke-violet" strokeWidth="2" />
        </svg>

        <div className="grid grid-cols-2 gap-x-5 gap-y-4 border-t border-edge px-5 py-4 sm:grid-cols-4">
          <HeroMetric label="Пройдено" value="4.6 км" accent />
          <HeroMetric label="Осталось" value="3.3 км" />
          <HeroMetric label="Скорость" value="4.9 км/ч" />
          <HeroMetric label="До цели" value="41 мин" />
        </div>
      </div>
      <figcaption className="mt-3 text-xs text-muted">Пример виджета · демонстрационные данные</figcaption>
    </figure>
  );
}

function HeroMetric({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted">
        {label}
      </span>
      <span
        className={`font-mono text-base font-semibold tabular-nums ${
          accent ? 'text-lime' : 'text-ink'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Демонстрация виджета
// ─────────────────────────────────────────────────────────────

/**
 * Большой макет OBS-оверлея с работающим переключателем состояний.
 * Требование промпта: показать и «В пути», и «Маршрут завершён».
 */
function OverlayShowcase() {
  const [state, setState] = useState<'active' | 'completed'>('active');

  return (
    <section id="widget" className="border-b border-edge bg-surface/40">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="flex max-w-2xl flex-col gap-4">
            <p className="text-sm font-medium text-muted">
              OBS Browser Source
            </p>
            <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
              Так выглядит виджет
            </h2>
            <p className="text-base leading-relaxed text-muted">
              Одна ссылка в OBS. Прозрачный фон, пять компоновок и точный
              контроль над тем, какие данные видны в кадре.
            </p>
          </div>

          {/* Переключатель состояний виджета */}
          <div
            className="flex gap-1 rounded-card border border-edge bg-base p-1"
            role="tablist"
            aria-label="Состояние виджета"
          >
            {(
              [
                { key: 'active' as const, label: 'В пути' },
                { key: 'completed' as const, label: 'Маршрут завершён' },
              ]
            ).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={state === key}
                className={`rounded-[0.625rem] px-3.5 py-2 text-xs font-semibold transition-colors ${
                  state === key
                    ? 'bg-lime text-base'
                    : 'text-muted hover:text-ink'
                }`}
                onClick={() => setState(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 overflow-hidden rounded-panel border border-edge bg-base">
          {/* Имитация кадра стрима: виджет лежит поверх «видео». */}
          <div className="relative aspect-[16/9] w-full bg-surface">

            <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-mono text-xs uppercase tracking-[0.2em] text-muted/40">
              Кадр трансляции
            </p>

            <div className="absolute left-5 top-5 w-[22rem] max-w-[calc(100%-2.5rem)]">
              {state === 'active' ? <DemoActiveWidget /> : <DemoCompletedWidget />}
            </div>
          </div>
        </div>

        <p className="mt-3 flex items-center gap-2">
          <span className="rounded border border-edge px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.18em] text-muted">
            Demo
          </span>
          <span className="text-xs text-muted">
            Значения условные. В эфире виджет показывает реальные данные с твоего
            GPS-источника или пометку об их отсутствии.
          </span>
        </p>
      </div>
    </section>
  );
}

function DemoActiveWidget() {
  return (
    <div className="overflow-hidden rounded-panel border border-edge/80 bg-base/85 backdrop-blur-sm">
      <div className="relative h-32 border-b border-edge/80 bg-surface">
        <svg viewBox="0 0 320 128" className="h-full w-full" aria-hidden>
          <rect width="320" height="128" fill="#080F20" />
          <path
            d="M 16 104 C 70 104, 82 62, 140 56 S 212 56, 250 24"
            fill="none"
            stroke="#9146FF"
            strokeWidth="2"
            strokeDasharray="5 5"
            opacity="0.55"
          />
          <path
            d="M 16 104 C 70 104, 82 62, 140 56"
            fill="none"
            stroke="#AFFF00"
            strokeWidth="2.75"
            strokeLinecap="round"
          />
          <circle cx="16" cy="104" r="3.5" fill="#050914" stroke="#AFFF00" strokeWidth="2" />
          <circle cx="250" cy="24" r="4" fill="#050914" stroke="#9146FF" strokeWidth="2" />
          <circle cx="140" cy="56" r="8" fill="#AFFF00" opacity="0.18" />
          <circle cx="140" cy="56" r="4" fill="#AFFF00" stroke="#050914" strokeWidth="1.5" />
        </svg>
      </div>

      <div className="flex flex-col gap-3.5 p-4">
        <div className="flex items-center gap-2">
          <Flag className="h-3.5 w-3.5 shrink-0 text-violet" aria-hidden />
          <span className="truncate text-sm font-semibold text-ink">
            Площадь Александра Невского
          </span>
        </div>

        <div className="grid grid-cols-3 gap-x-4 gap-y-3">
          <DemoMetric icon={Flag} label="Пройдено" value="4.6 км" accent="lime" />
          <DemoMetric icon={RouteIcon} label="Осталось" value="3.3 км" accent="violet" />
          <DemoMetric icon={Timer} label="В пути" value="56 мин" />
          <DemoMetric icon={Thermometer} label="Температура" value="7°C" />
          <DemoMetric icon={Wind} label="Ветер" value="4.2 м/с, ЮЗ" />
          <DemoMetric icon={Footprints} label="Шаги" value="6 240" />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between">
            <span className="font-mono text-xs font-semibold tabular-nums text-lime">
              4.6 км
            </span>
            <span className="font-mono text-xs font-bold tabular-nums text-ink">58.2%</span>
            <span className="font-mono text-xs tabular-nums text-violet">7.9 км</span>
          </div>
          <div className="h-2.5 overflow-hidden rounded-full border border-edge/70 bg-base/70">
            <div className="h-full w-[58%] rounded-full bg-lime" />
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoCompletedWidget() {
  return (
    <div className="flex flex-col items-center gap-5 rounded-panel border border-lime/30 bg-base/85 px-7 py-8 backdrop-blur-sm">
      <span className="relative flex h-14 w-14 items-center justify-center">
        <span className="absolute inset-0 rounded-full bg-lime-dim" aria-hidden />
        <svg viewBox="0 0 24 24" className="relative h-10 w-10 text-lime" aria-hidden>
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
          <path
            d="m8 12.5 2.5 2.5L16 9"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>

      <div className="flex flex-col items-center gap-1.5 text-center">
        <h3 className="text-2xl font-extrabold uppercase tracking-[0.06em] text-ink">
          Маршрут завершён!
        </h3>
        <p className="font-mono text-[0.625rem] uppercase tracking-[0.16em] text-muted">
          Площадь Александра Невского
        </p>
      </div>

      <dl className="grid w-full grid-cols-2 gap-5 sm:grid-cols-4">
        {[
          { label: 'Пройдено', value: '7.9 км', accent: true },
          { label: 'В движении', value: '1 ч 38 мин', accent: false },
          { label: 'Шаги', value: '10 820', accent: false },
          { label: 'Средняя', value: '4.8 км/ч', accent: false },
        ].map(({ label, value, accent }) => (
          <div key={label} className="flex flex-col items-center gap-1 text-center">
            <dt className="stat-label">{label}</dt>
            <dd
              className={`font-mono text-lg font-semibold tabular-nums ${
                accent ? 'text-lime' : 'text-ink'
              }`}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex w-full items-center justify-center gap-6 border-t border-edge pt-4">
        <span className="flex items-center gap-2">
          <Thermometer className="h-3.5 w-3.5 text-muted" aria-hidden />
          <span className="font-mono text-sm tabular-nums text-ink">6°C</span>
        </span>
        <span className="flex items-center gap-2">
          <Wind className="h-3.5 w-3.5 text-muted" aria-hidden />
          <span className="font-mono text-sm tabular-nums text-ink">3.8 м/с, З</span>
        </span>
      </div>
    </div>
  );
}

function DemoMetric({
  icon: Icon,
  label,
  value,
  accent = 'default',
}: {
  icon: typeof Flag;
  label: string;
  value: string;
  accent?: 'default' | 'lime' | 'violet';
}) {
  const color =
    accent === 'lime' ? 'text-lime' : accent === 'violet' ? 'text-violet' : 'text-ink';

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 shrink-0 text-muted" aria-hidden />
        <span className="truncate text-[0.5625rem] font-semibold uppercase tracking-[0.1em] text-muted">
          {label}
        </span>
      </span>
      <span className={`font-mono text-sm font-bold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Создан для IRL
// ─────────────────────────────────────────────────────────────

/**
 * Последовательность шагов как техническая схема с маршрутной линией,
 * а не четыре одинаковые карточки — прямое требование промпта.
 */
function BuiltForIrl() {
  const steps = [
    {
      icon: Smartphone,
      title: 'Телефон как GPS-источник',
      text: 'Открываешь страницу трекера по персональной ссылке. Отдельное устройство тоже подойдёт.',
    },
    {
      icon: Navigation,
      title: 'Идёшь по маршруту',
      text: 'Координаты уходят на сервер пакетами. Без сети точки копятся и отправляются позже.',
    },
    {
      icon: Satellite,
      title: 'Сервер считает прогресс',
      text: 'Позиция прижимается к пешеходной геометрии, выбросы GPS отбрасываются фильтром.',
    },
    {
      icon: MonitorPlay,
      title: 'Зрители видят путь',
      text: 'Виджет в OBS обновляется по WebSocket: без перезагрузки и мерцания в кадре.',
    },
  ];

  return (
    <section className="border-b border-edge">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-24">
        <div className="flex max-w-2xl flex-col gap-4">
          <p className="text-sm font-medium text-muted">
            Создан для IRL
          </p>
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
            Как подключить
          </h2>
        </div>

        {/* Маршрутная линия с контрольными точками. */}
        <ol className="relative mt-12 flex flex-col gap-10 lg:flex-row lg:gap-0">
          <span
            className="absolute left-[1.0625rem] top-2 bottom-2 w-px bg-edge lg:left-0 lg:right-0 lg:top-[1.0625rem] lg:bottom-auto lg:h-px lg:w-auto"
            aria-hidden
          />

          {steps.map(({ icon: Icon, title, text }, index) => (
            <li
              key={title}
              className="relative flex gap-5 lg:flex-1 lg:flex-col lg:gap-5 lg:pr-8"
            >
              <span
                className={`relative z-10 flex h-[2.125rem] w-[2.125rem] shrink-0 items-center justify-center rounded-full border bg-base ${
                  index === 0
                    ? 'border-lime/50'
                    : index === steps.length - 1
                      ? 'border-violet/50'
                      : 'border-edge'
                }`}
              >
                <Icon
                  className={`h-4 w-4 ${
                    index === 0
                      ? 'text-lime'
                      : index === steps.length - 1
                        ? 'text-violet'
                        : 'text-muted'
                  }`}
                  aria-hidden
                />
              </span>

              <div className="flex min-w-0 flex-col gap-2">
                <span className="font-mono text-[0.625rem] uppercase tracking-[0.18em] text-muted">
                  Шаг {index + 1}
                </span>
                <h3 className="text-base font-bold leading-snug text-ink">{title}</h3>
                <p className="text-sm leading-relaxed text-muted">{text}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* Честная оговорка вместо обещания интеграций, которых нет. */}
        <p className="mt-10 max-w-3xl border-l-2 border-edge pl-5 text-sm leading-relaxed text-muted">
          Прямой интеграции со стриминговыми приложениями нет: GPS Tracker
          получает координаты от телефона или совместимого трекера по защищённому
          endpoint. Шаги доступны только если источник их предоставляет — браузер
          системный шагомер не отдаёт.
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Приборная панель телеметрии
// ─────────────────────────────────────────────────────────────

/**
 * Крупные числа, тонкие разделители, выраженная иерархия.
 * Главный показатель вынесен отдельно и крупнее остальных.
 */
function TelemetryBoard() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  /** Появление при прокрутке: одна осмысленная анимация, без цикла. */
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const secondary = [
    { icon: Thermometer, label: 'Температура', value: '7', unit: '°C' },
    { icon: Wind, label: 'Ветер', value: '4.2', unit: 'м/с' },
    { icon: Footprints, label: 'Шаги', value: '6 240', unit: '' },
    { icon: Gauge, label: 'Темп', value: '4.9', unit: 'км/ч' },
    { icon: Timer, label: 'В движении', value: '56', unit: 'мин' },
    { icon: Satellite, label: 'Точность GPS', value: '±6', unit: 'м' },
  ];

  return (
    <section id="telemetry" className="border-b border-edge bg-surface/40">
      <div
        ref={ref}
        className={`mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-24 ${
          visible ? 'animate-fade-up' : 'opacity-0'
        }`}
      >
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="flex max-w-xl flex-col gap-4">
            <p className="text-sm font-medium text-muted">
              Телеметрия
            </p>
            <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
              Показатели маршрута
            </h2>
          </div>

          <span className="flex items-center gap-2">
            <span className="rounded border border-edge px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.18em] text-muted">
              Demo
            </span>
            <span className="text-xs text-muted">Пример показаний</span>
          </span>
        </div>

        <div className="mt-12 grid gap-px overflow-hidden rounded-panel border border-edge bg-edge lg:grid-cols-[minmax(0,1.3fr)_minmax(0,2fr)]">
          {/* Главный показатель крупнее остальных. */}
          <div className="flex flex-col justify-between gap-8 bg-base p-7 lg:p-9">
            <div className="flex flex-col gap-2">
              <span className="stat-label">Пройдено по маршруту</span>
              <span className="font-mono text-metric-lg font-bold tabular-nums text-lime">
                4.6
                <span className="ml-2 text-xl font-semibold text-muted">км</span>
              </span>
            </div>

            <div className="flex flex-col gap-2.5">
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-xs tabular-nums text-muted">0</span>
                <span className="font-mono text-sm font-bold tabular-nums text-ink">
                  58.2%
                </span>
                <span className="font-mono text-xs tabular-nums text-muted">7.9 км</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-elevated">
                <div
                  className={`h-full rounded-full bg-lime transition-[width] duration-1000 ease-telemetry ${
                    visible ? 'w-[58%]' : 'w-0'
                  }`}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 border-t border-edge pt-6">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-lime" aria-hidden />
              <span className="coord truncate">59.93284, 30.34012</span>
            </div>
          </div>

          {/* Вторичные показатели: плотная сетка с тонкими разделителями. */}
          <div className="grid grid-cols-2 gap-px bg-edge sm:grid-cols-3">
            {secondary.map(({ icon: Icon, label, value, unit }) => (
              <div key={label} className="flex flex-col gap-2 bg-base p-5 lg:p-6">
                <span className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />
                  <span className="stat-label truncate">{label}</span>
                </span>
                <span className="font-mono text-2xl font-bold tabular-nums text-ink">
                  {value}
                  {unit ? (
                    <span className="ml-1 text-sm font-semibold text-muted">{unit}</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Персонализация
// ─────────────────────────────────────────────────────────────

/** Композиция из фрагментов реального интерфейса, а не абстрактных иконок. */
function Personalization() {
  return (
    <section className="border-b border-edge">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:items-center lg:gap-16 lg:px-10 lg:py-24">
        <div className="flex flex-col gap-6">
          <p className="text-sm font-medium text-muted">
            Персонализация
          </p>

          <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
            Настройки маршрута и виджета
          </h2>

          <ol className="flex flex-col gap-5">
            {[
              {
                title: 'Вход через Twitch',
                text: 'Профиль создаётся автоматически. Twitch используется только для идентификации — координаты он не предоставляет.',
              },
              {
                title: 'Старт и цель',
                text: 'Геолокация, поиск по адресу, выбор на карте или ввод координат вручную.',
              },
              {
                title: 'Настройка виджета',
                text: 'Пять компоновок и переключатели для каждого блока: карта, погода, ветер, шаги, координаты.',
              },
              {
                title: 'Ссылка для OBS',
                text: 'Персональный адрес с секретным токеном. Отключается и перегенерируется в один клик.',
              },
            ].map(({ title, text }, index) => (
              <li key={title} className="flex gap-4">
                <span className="mt-0.5 font-mono text-sm font-bold tabular-nums text-lime">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="flex flex-col gap-1">
                  <h3 className="text-sm font-bold text-ink">{title}</h3>
                  <p className="text-sm leading-relaxed text-muted">{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Фрагменты интерфейса: настройки видимости и ссылка. */}
        <div className="flex flex-col gap-4">
          <div className="panel flex flex-col gap-3.5 p-5">
            <span className="stat-label">Что показывать зрителям</span>

            {[
              { label: 'Карта маршрута', on: true },
              { label: 'Погода и ветер', on: true },
              { label: 'Шаги', on: true },
              { label: 'Точные координаты', on: false },
            ].map(({ label, on }) => (
              <div key={label} className="flex items-center justify-between gap-4">
                <span className="text-sm text-ink">{label}</span>
                <span
                  className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
                    on ? 'bg-lime' : 'bg-elevated border border-edge'
                  }`}
                  role="img"
                  aria-label={on ? 'включено' : 'выключено'}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full transition-all ${
                      on ? 'left-[1.125rem] bg-base' : 'left-0.5 bg-muted'
                    }`}
                  />
                </span>
              </div>
            ))}
          </div>

          <div className="panel flex flex-col gap-3 p-5">
            <span className="stat-label">Ссылка для OBS</span>
            <div className="flex items-center gap-2 rounded-card border border-edge bg-base px-3 py-2.5">
              <MonitorPlay className="h-3.5 w-3.5 shrink-0 text-violet" aria-hidden />
              <span className="truncate font-mono text-xs text-muted">
                /overlay/a7f3c9…
              </span>
            </div>
            <p className="text-xs leading-relaxed text-muted">
              Токен хранится в виде хэша. Ссылка не даёт доступа к личному
              кабинету, истории и настройкам.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Финальный экран
// ─────────────────────────────────────────────────────────────

function FinalCta() {
  return (
    <section className="relative overflow-hidden border-b border-edge">
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-8 px-5 py-20 text-center lg:py-28">
        <h2 className="max-w-3xl text-3xl font-extrabold leading-[1.1] tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Подключи GPS Tracker к OBS.
        </h2>

        <Link to="/login" className="btn-primary px-6 py-3.5 text-base">
          Создать свой маршрут
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-5 px-5 py-8 lg:px-10">
      <Logo compact />

      <p className="font-mono text-[0.625rem] uppercase tracking-[0.16em] text-muted">
        Карты © OpenStreetMap · Погода Open-Meteo
      </p>

      <Link to="/login" className="text-sm text-muted transition-colors hover:text-ink">
        Войти через Twitch
      </Link>
    </footer>
  );
}
